import React, { useEffect, useState } from "react";
import { DndContext, closestCorners, pointerWithin, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors, useDroppable } from "@dnd-kit/core";
import { SortableContext, useSortable, rectSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import api, { apiError } from "@/lib/api";
import { PROJECT_STATUS } from "@/lib/taskUi";
import { FolderKanban, Users, GripVertical, ChevronDown, ChevronRight, Plus, Pencil } from "lucide-react";
import { toast } from "sonner";

export const UNCAT = "__none__";
const STATUS_COLORS = { planning: "bg-slate-100 text-slate-600", active: "bg-sky-100 text-sky-700",
  on_hold: "bg-amber-100 text-amber-700", completed: "bg-emerald-100 text-emerald-700", cancelled: "bg-rose-100 text-rose-600" };
const COLLAPSE_KEY = "tr.projects.collapsed";

function ProjectCard({ p, canDrag, onOpen }) {
  const drag = canDrag && p.can_edit;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.id, disabled: !drag });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      onClick={() => onOpen(p)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onOpen(p)} data-testid={`project-card-${p.id}`}
      className="group text-left rounded-xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md hover:border-brand/40 transition-shadow cursor-pointer">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          {drag && <button type="button" {...attributes} {...listeners} onClick={(e) => e.stopPropagation()} aria-label="Drag to reorder"
            className="cursor-grab touch-none text-slate-300 hover:text-slate-500 -ml-1.5" data-testid={`project-drag-${p.id}`}><GripVertical className="w-4 h-4" /></button>}
          <FolderKanban className="w-5 h-5 text-brand" />
        </div>
        <div className="flex items-center gap-1.5">
          {p.is_personal && <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 bg-violet-50 text-violet-700" data-testid={`project-personal-${p.id}`}>Personal</span>}
          <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${p.archived ? "bg-slate-200 text-slate-600" : STATUS_COLORS[p.status]}`}>{p.archived ? "Archived" : PROJECT_STATUS[p.status]}</span>
        </div>
      </div>
      <h3 className="font-semibold text-slate-900 truncate">{p.name}</h3>
      <p className={`text-xs truncate ${p.person_name ? "text-slate-500 font-medium" : "text-slate-400 font-mono"}`} data-testid={`project-card-person-${p.id}`}>{p.person_name || p.human_id}</p>
      <div className="mt-3">
        <div className="flex items-center justify-between text-xs text-slate-500 mb-1"><span>Progress</span><span className="font-semibold">{p.stats.progress}%</span></div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-brand rounded-full" style={{ width: `${p.stats.progress}%` }} /></div>
      </div>
      <div className="flex items-center gap-3 mt-3 text-xs text-slate-400">
        <span>{p.stats.completed_tasks}/{p.stats.total_tasks} tasks</span>
        {p.stats.overdue_tasks > 0 && <span className="text-rose-600">{p.stats.overdue_tasks} overdue</span>}
        <span className="flex items-center gap-1 ml-auto"><Users className="w-3 h-3" />{(p.team || []).length}</span>
      </div>
    </div>
  );
}

function Group({ gkey, title, ids, byId, collapsed, onToggle, canDrag, onOpen, onAdd, onEdit }) {
  const { setNodeRef, isOver } = useDroppable({ id: gkey });
  const n = ids.length;
  return (
    <section data-testid={`project-group-${gkey}`}>
      <div className="flex items-center gap-2 mb-2.5 border-b border-slate-200 pb-1.5">
        <button onClick={onToggle} className="flex items-center gap-1.5 text-left" data-testid={`project-group-toggle-${gkey}`}>
          {collapsed ? <ChevronRight className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          <span className="text-xs font-bold tracking-wider uppercase text-slate-700">{title}</span>
          <span className="text-xs text-slate-400" data-testid={`project-group-count-${gkey}`}>· {n} Project{n === 1 ? "" : "s"}</span>
        </button>
        <div className="ml-auto flex items-center gap-1">
          {onEdit && <button onClick={onEdit} className="p-1 text-slate-300 hover:text-brand" title="Edit category" data-testid={`project-group-edit-${gkey}`}><Pencil className="w-3.5 h-3.5" /></button>}
          <button onClick={onAdd} className="flex items-center gap-1 text-xs text-slate-500 hover:text-brand px-1.5 py-1" data-testid={`project-group-add-${gkey}`}><Plus className="w-3.5 h-3.5" />Add Project</button>
        </div>
      </div>
      {!collapsed && (
        <SortableContext items={ids} strategy={rectSortingStrategy}>
          <div ref={setNodeRef} className={`grid gap-4 sm:grid-cols-2 lg:grid-cols-3 min-h-[3.5rem] rounded-xl transition-colors ${isOver ? "bg-brand/5" : ""}`}>
            {ids.map((id) => <ProjectCard key={id} p={byId[id]} canDrag={canDrag} onOpen={onOpen} />)}
            {!n && <p className="sm:col-span-2 lg:col-span-3 text-xs text-slate-400 border border-dashed border-slate-200 rounded-xl py-8 text-center">{canDrag ? "Drag a project here" : "No projects"}</p>}
          </div>
        </SortableContext>
      )}
    </section>
  );
}

const build = (projects, keys) => {
  const g = Object.fromEntries(keys.map((k) => [k, []]));
  projects.forEach((p) => { (g[p.category_id] || g[UNCAT]).push(p.id); });
  return g;
};

export function ProjectBoard({ projects, cats, canDrag, showEmpty, onOpen, onAdd, onEditCategories, onSaved }) {
  const keys = [...cats.map((c) => c.id), UNCAT];
  const [groups, setGroups] = useState(() => build(projects, keys));
  const [collapsed, setCollapsed] = useState(() => { try { return JSON.parse(localStorage.getItem(COLLAPSE_KEY) || "{}"); } catch { return {}; } });
  useEffect(() => { setGroups(build(projects, [...cats.map((c) => c.id), UNCAT])); }, [projects, cats]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const byId = Object.fromEntries(projects.map((p) => [p.id, p]));
  const find = (id) => (id in groups ? id : Object.keys(groups).find((k) => groups[k].includes(id)));
  const toggle = (k) => { const n = { ...collapsed, [k]: !collapsed[k] }; setCollapsed(n); localStorage.setItem(COLLAPSE_KEY, JSON.stringify(n)); };

  const over = ({ active, over: o }) => {
    if (!o) return;
    const from = find(active.id), to = find(o.id);
    if (!from || !to || from === to) return;
    setGroups((g) => {
      const dest = g[to].filter((x) => x !== active.id);
      const idx = o.id in g ? dest.length : Math.max(0, dest.indexOf(o.id));
      dest.splice(idx, 0, active.id);
      return { ...g, [from]: g[from].filter((x) => x !== active.id), [to]: dest };
    });
  };
  const end = async ({ active, over: o }) => {
    const to = find(active.id);
    if (!to) return;
    let ids = groups[to];
    if (o && !(o.id in groups) && ids.includes(o.id) && o.id !== active.id) ids = arrayMove(ids, ids.indexOf(active.id), ids.indexOf(o.id));
    setGroups((g) => ({ ...g, [to]: ids }));
    const movable = ids.filter((id) => byId[id]?.can_edit);
    try {
      await api.post("/projects/reorder", { category_id: to === UNCAT ? null : to, ids: movable });
      toast.success(byId[active.id]?.category_id !== (to === UNCAT ? null : to) ? "Project moved" : "Order saved");
      onSaved();
    } catch (e) { toast.error(apiError(e)); onSaved(); }
  };

  const titles = { ...Object.fromEntries(cats.map((c) => [c.id, c.is_personal ? `${c.name} · Personal` : c.name])), [UNCAT]: "Uncategorized" };
  const collide = (args) => {
    const hit = pointerWithin(args).find((c) => c.id in groups && !groups[c.id].filter((x) => x !== args.active.id).length);
    return hit ? [hit] : closestCorners(args);
  };
  const canEditCat = Object.fromEntries(cats.map((c) => [c.id, c.can_manage]));
  return (
    <DndContext sensors={sensors} collisionDetection={collide} onDragOver={over} onDragEnd={end}>
      <div className="space-y-7" data-testid="projects-grid">
        {keys.filter((k) => showEmpty || groups[k]?.length).map((k) => (
          <Group key={k} gkey={k} title={titles[k]} ids={(groups[k] || []).filter((id) => byId[id])} byId={byId} collapsed={!!collapsed[k]} onToggle={() => toggle(k)}
            canDrag={canDrag} onOpen={onOpen} onAdd={() => onAdd(k === UNCAT ? null : k)} onEdit={canEditCat[k] ? onEditCategories : null} />
        ))}
      </div>
    </DndContext>
  );
}
